/**
 * Generates HTML email template for account verification.
 * Sent when a user registers and needs to verify their email address.
 * Uses shared layout from emailLayout.js for header/footer.
 */

import { spacer, emailDocOpen, emailDocClose, emailHeader, emailFooter } from '../emailLayout';

/**
 * Build the verify account email HTML
 *
 * @param {Object} params
 * @param {string} params.firstName - User's first name
 * @param {string} params.lastName - User's last name
 * @param {string} params.email - User's email address
 * @param {string} params.verificationUrl - URL for the user to verify their account
 * @returns {string} HTML email body
 */
export const buildVerifyAccountEmailHtml = ({
  firstName,
  lastName,
  email,
  verificationUrl,
  organization,
}) => {
  const memberName = [firstName, lastName].filter(Boolean).join(' ');

  return `${emailDocOpen({ title: 'Verify Your Account' })}

${emailHeader({ title: 'Verify Your Account', organization, isBroker: true })}

${spacer(0)}

<!-- Member & Email -->
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;">
  <tr>
    <td style="padding:24px 32px 0;">
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
        <tr>
          <td style="width:50%;vertical-align:top;">
            <p style="margin:0 0 2px;font-size:12px;text-transform:uppercase;color:#6B7280;letter-spacing:0.5px;font-weight:600;">User</p>
            <p style="margin:0;font-size:14px;color:#111827;font-weight:600;">${memberName}</p>
          </td>
          <td style="width:50%;vertical-align:top;text-align:right;">
            <p style="margin:0 0 2px;font-size:12px;text-transform:uppercase;color:#6B7280;letter-spacing:0.5px;font-weight:600;">Signed up using</p>
            <p style="margin:0;font-size:14px;color:#111827;font-weight:600;">${email}</p>
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td style="padding:16px 32px 0;">
      <div style="border-top:1px solid #EBEBEB;"></div>
    </td>
  </tr>
</table>

${spacer(0)}

<!-- Verification Message -->
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;">
  <tr>
    <td style="padding:24px 32px 0;">
      <table role="presentation" cellpadding="0" cellspacing="0">
        <tr>
          <td style="vertical-align:top;padding-right:0;">
            <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='52' height='52' viewBox='0 0 52 52'%3E%3Ccircle cx='26' cy='26' r='26' fill='%23E8F5E9'/%3E%3Cg transform='translate(14,14)' fill='none' stroke='%234CAF50' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z'/%3E%3Cpolyline points='22 6 12 13 2 6'/%3E%3C/g%3E%3C/svg%3E" width="52" height="52" alt="" style="display:block;" />
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td style="padding:16px 32px 32px;">
      <p style="margin:0;font-size:14px;color:#374151;line-height:1.7;">
        We're all set to get you signed up.<br />
        Just verify your email address by cliking the button below.
      </p>
    </td>
  </tr>
</table>

${spacer(0)}

<!-- CTA Button -->
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;">
  <tr>
    <td style="padding:0 32px 32px;">
      <table role="presentation" cellpadding="0" cellspacing="0">
        <tr>
          <td style="background-color:#22315e;border-radius:24px;">
            <a href="${verificationUrl}" target="_blank" style="display:inline-block;padding:12px 28px;font-size:14px;color:#FFFFFF;text-decoration:none;font-weight:500;font-family:'Rethink Sans',sans-serif;">
              Verify Email
            </a>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>

${spacer(20)}

${emailFooter({ organization })}

${emailDocClose()}`;
};
